import BreadCrumb from "Components/Common/BreadCrumb";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { useDebounce } from "Components/Hooks/useDebounce";
import CustomFormModal, { Field } from "Components/Modals/CustomFormModal";
import SubcategoriesTable from "Components/Settings/Subcategories/SubcategoriesTable";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { Container } from "reactstrap";
import { getCategories, manageSubCategory } from "slices/thunks";
import * as Yup from "yup";
import {
  VALIDATION_LIMITS,
  VALIDATION_MESSAGES,
  createNameValidation,
} from "Components/constants/validation";

export interface SubCategoriesType {
  id: string;
  subCategory: string;
  category: string;
  items: number;
}
export interface SubCategoryType {
  id?: number;
  category?: number;
  subCategoryName: string;
}

const SubCategories = () => {
  const [isAddSubCategory, setIsSubCategory] = useState<boolean>(false);
  const [isEditSubCategory, setIsEditSubCategory] = useState<boolean>(false);
  const [categorySearch, setCategorySearch] = useState("");
  const debounceCategorySearch = useDebounce(categorySearch, 500);

  const dispatch = useAppDispatch();

  const { categories, isLoadingCategory } = useAppSelector(
    (stat) => stat.category
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const validation = useFormik<SubCategoryType>({
    enableReinitialize: true,
    initialValues: {
      category: undefined,
      subCategoryName: "",
    },
    validationSchema: Yup.object({
      category: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("Category")),
      subCategoryName: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("Subcategory Name"))
        .min(
          VALIDATION_LIMITS.NAME.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH(
            "Subcategory Name",
            VALIDATION_LIMITS.NAME.MIN
          )
        )
        .max(
          VALIDATION_LIMITS.NAME.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "Subcategory Name",
            VALIDATION_LIMITS.NAME.MAX
          )
        )
        .test(
          "valid-subcategory-name",
          createNameValidation("Subcategory Name").message,
          createNameValidation("Subcategory Name").test
        ),
    }),
    onSubmit: async (values, { resetForm }) => {
      if (!values.category) {
        return;
      }
      try {
        setIsSubmitting(true);
        if (values.id) {
          await dispatch(
            manageSubCategory({
              id: values.id,
              category_id: values.category,
              name: values.subCategoryName?.trim(),
            })
          )?.unwrap();
        } else {
          await dispatch(
            manageSubCategory({
              category_id: values.category,
              name: values.subCategoryName?.trim(),
            })
          )?.unwrap();
        }
        resetForm();
        setIsSubCategory(false);
        setIsEditSubCategory(false);
      } catch (error) {
        console.error("Error managing subcategory:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const CategoryFields: Field[] = [
    {
      name: "category",
      label: "Category",
      type: "select",
      placeholder: "Search or select a category",
      options: categories.map((cat) => ({
        label: cat.name,
        value: cat.id,
      })),
      onSearch: (e) => {
        setCategorySearch(e);
      },
      isLoadingOptions: isLoadingCategory,
    },
    {
      name: "subCategoryName",
      label: "Subcategory Name",
      type: "text",
      placeholder: "Subcategory name",
    },
  ];

  const handleSubCategoryModal = (isAdd: boolean) => {
    if (isAdd) {
      setIsSubCategory((prev) => !prev);
    } else {
      setIsEditSubCategory((prev) => !prev);
    }
  };

  const handleEditSubCategory = (cat: any) => {
    setIsEditSubCategory((prev) => !prev);
    validation.setFieldValue("category", cat?.category?.id);
    validation.setFieldValue("subCategoryName", cat?.name);
    validation.setFieldValue("id", cat?.id);
  };

  useEffect(() => {
    dispatch(getCategories({ search: categorySearch }));
  }, [debounceCategorySearch]);

  document.title = "Subcategories | EaziQuote";

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Subcategories"
          pageTitle="Settings"
          url="/settings"
        />

        <SubcategoriesTable
          onAddSubCategory={handleSubCategoryModal?.bind(null, true)}
          onEditSubCategory={handleEditSubCategory}
        />

        <CustomFormModal
          isOpen={isAddSubCategory || isEditSubCategory}
          onCancel={
            isAddSubCategory
              ? handleSubCategoryModal?.bind(null, true)
              : handleSubCategoryModal?.bind(null, false)
          }
          fields={CategoryFields}
          title={isAddSubCategory ? "Add Subcategory" : "Edit Subcategory"}
          submitText="Save Subcategory"
          validation={validation}
          isLoading={isSubmitting}
        />
      </Container>
    </div>
  );
};

export default SubCategories;
